import BaseApi from "./BaseApi";
import { setTokens, clearTokens } from "../config/api.config";
import { logError } from "../utils/errorLogger";
class UserApi extends BaseApi {
  constructor() {
    super("users"); // Pass 'users' as the endpoint for BaseApi
  }


  // Create a new entity
  async create(data, useProtected = true) {
    const api = this.getApiInstance(useProtected);
    const response = await api.post(`/${this.endpoint}/register`, data);
    return response.data;
  }

  // User login - authenticate and store tokens
  async login(credentials) {
    try {
      const response = await this.getApiInstance(false).post(
        `/${this.endpoint}/login`,
        credentials
      );

      if (response.data?.requires2FA) {
        return response.data;
      }

      const { accessToken, refreshToken, user } = response.data;

      // Store tokens in localStorage
      setTokens(accessToken, refreshToken);
      localStorage.setItem("user", JSON.stringify(user));
      if (response.data.permissions) {
        localStorage.setItem("permissions", JSON.stringify(response.data.permissions));
      }

      // Return full response object to match expected structure
      return {
        accessToken,
        refreshToken,
        user,
        permissions: response.data.permissions,
      };
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async verify2FALogin({ twoFactorToken, code }) {
    try {
      const response = await this.getApiInstance(false).post(
        `/${this.endpoint}/login/2fa`,
        { twoFactorToken, code }
      );

      const { accessToken, refreshToken, user } = response.data;
      setTokens(accessToken, refreshToken);
      localStorage.setItem("user", JSON.stringify(user));
      if (response.data.permissions) {
        localStorage.setItem("permissions", JSON.stringify(response.data.permissions));
      }

      return {
        accessToken,
        refreshToken,
        user,
        permissions: response.data.permissions,
      };
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async get2FAStatus() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/me/2fa`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async setup2FA() {
    try {
      const response = await this.getApiInstance(true).post(`/${this.endpoint}/me/2fa/setup`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async confirm2FA(code) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/me/2fa/confirm`,
        { code }
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async disable2FA({ password, code }) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/me/2fa/disable`,
        { password, code }
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Refresh access token using the refresh token
  async refreshToken() {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken)
      throw new Error("Le jeton de rafraîchissement est manquant");

    try {
      const response = await this.getApiInstance(false).post(
        `/${this.endpoint}/refresh-token`,
        { refreshToken }
      );
      const { accessToken, refreshToken: newRefreshToken } = response.data;

      // Update tokens in localStorage
      setTokens(accessToken, newRefreshToken);

      return accessToken;
    } catch (error) {
      clearTokens();
      logError(error);
      throw error;
    }
  }

  // User logout - clear tokens from localStorage
  logout() {
    clearTokens(); // Remove tokens from localStorage
  }

  // Fetch user permissions
  async getUserPermissions(userId) {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/${userId}/permissions`
      );
      return response.data; // Return permissions for the user
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Update user permissions
  async updateUserPermissions(userId, permissions) {
    try {
      const response = await this.getApiInstance(true).put(
        `/${this.endpoint}/${userId}/permissions`,
        permissions
      );
      return response.data; // Success message
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Change user password
  async changePassword(userId, newPassword) {
    try {
      const response = await this.getApiInstance(true).put(
        `/${this.endpoint}/${userId}/change-password`,
        { newPassword }
      );
      return response.data; // Success message
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Request password reset by email
  async requestPasswordReset(email) {
    try {
      const response = await this.getApiInstance(false).post(
        `/${this.endpoint}/request-password-reset`,
        { email }
      );
      return response.data; // Success message
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Reset password with new password using user ID
  async resetPassword(userId, newPassword) {
    try {
      const response = await this.getApiInstance(false).post(
        `/${this.endpoint}/reset-password/${userId}`,
        { newPassword }
      );
      return response.data; // Success message
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get user by ID
  async getById(userId) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/${userId}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get all users
  async getAll() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Update user
  async update(userId, data) {
    try {
      const response = await this.getApiInstance(true).put(`/${this.endpoint}/${userId}`, data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Delete user
  async delete(userId) {
    try {
      const response = await this.getApiInstance(true).delete(`/${this.endpoint}/${userId}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get payment methods by user ID
  async getPaymentMethodsByUserId(userId) {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/payment-methods-by-user-id/${userId}`
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
  // Get partners list
  async getPartners() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/pv-partners/for-user`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
}

export default new UserApi();
